﻿<template>
  <div class="filter-search-bar">
    <div class="search-container">
      <IonSearchbar
        :search-icon="filterOutline"
        :model-value="modelValue"
        :placeholder="placeholder"
        :debounce="debounce"
        @ionInput="onInput"
        @ionClear="onClear"
        @ionBlur="onBlur"
        class="search-input"
        ref="searchBarRef"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { IonSearchbar } from '@ionic/vue';
import { filterOutline } from 'ionicons/icons';

// Props
interface Props {
  modelValue?: string;
  placeholder?: string;
  debounce?: number;
  showFilter?: boolean;
}

withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: 'Rechercher...',
  debounce: 300,
  showFilter: true,
});

// Events
const emit = defineEmits<{
  'update:modelValue': [value: string];
  input: [event: CustomEvent];
  clear: [event: CustomEvent];
  'filter-click': [];
  'search-active': [isActive: boolean];
}>();

// State
const isSearchActive = ref(false);
const searchBarRef = ref<any>(null);

// Methods
const onInput = (event: CustomEvent) => {
  const value = (event.target as any)?.value || '';
  emit('update:modelValue', value);
  emit('input', event);
};

const onClear = (event: CustomEvent) => {
  emit('update:modelValue', '');
  emit('clear', event);
};

const onBlur = () => {
  // Fermer la barre de recherche si elle est vide au bout d'un délai
  setTimeout(() => {
    isSearchActive.value = false;
    emit('search-active', false);
  }, 150); // Petit délai pour permettre les clics sur les résultats
};
</script>

<style scoped>
.filter-search-bar {
  width: 100%;
}

.search-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.filter-icon {
  font-size: 1.25rem;
  color: var(--ion-color-light-shade);
  cursor: pointer;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.filter-icon:hover {
  color: var(--ion-color-light);
}

.search-bar-container {
  flex: 1;
  animation: slideIn 0.3s ease-out;
}

.search-input {
  width: 100%;
  --background: var(--ion-background-color);
  --color: var(--ion-text-color);
  --placeholder-color: var(--ion-text-color-secondary);
  --icon-color: var(--ion-color-primary);
  --clear-button-color: var(--ion-color-primary);
  --border-radius: 3vmin;
  --box-shadow: none;
  border: 1px solid var(--ion-input-border-color);
  border-radius: 3vmin;
  padding: 0;
}

/* Animation pour l'apparition de la barre de recherche */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
    width: 0;
  }
  to {
    opacity: 1;
    transform: translateX(0);
    width: 100%;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .search-container {
    gap: 0.25rem;
  }

  .filter-icon {
    font-size: 1.1rem;
  }
}
</style>
